import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { Alert, ERP_SYSTEM_CONNECTION_TYPE, isPhone, SourceSystems, isTabletOrWeb } from '@helpers';

import TouchableElement from '@components/common/touchableElement';
import LineButton from '@components/common/lineSelector';
import { SmallLoading } from '@components/loading';

import { font } from '@styles/fonts';

import { SelectTypeProps, SystemType } from '@components/settings/v2/structure/connector/productionConnectorSetup/interfaces';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

const SelectConnectorType: React.FC<SelectTypeProps> = ({ C, setMode, MODES, updateData, goBack, erpSourceSystemTypes }) => {
  const [list, setList] = useState<SystemType[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SystemType | null>(null);
  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      if (isError) {
        setIsError(false);
      }

      const systemTypes = await erpSourceSystemTypes({
        filter: `?filter=not(code eq '${SourceSystems.MKG}' and sourceSystemConnectionType eq '${ERP_SYSTEM_CONNECTION_TYPE.MKG_ODBC}') `,
      });
      if (systemTypes.length === 0) {
        Alert({ title: i18n.t('Something didn’t work') });
        setIsError(true);
      } else {
        setList(systemTypes);
      }

      setLoaded(true);
    })();
  }, [erpSourceSystemTypes]);

  return (
    <View style={{ paddingBottom: scale(20), paddingHorizontal: isPhone ? 0 : scale(15) }}>
      <View>
        <View
          style={{
            width: '100%',
            zIndex: 1000,
            height: scale(58),
          }}
        >
          <View style={{ position: 'absolute', zIndex: 1000, left: 0 }}>
            <TouchableElement
              onPress={() => {
                updateData({
                  selectedType: null,
                });
                goBack();
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15), paddingHorizontal: isPhone ? scale(15) : 0, flexDirection: 'row' }]}>
                  <Text style={[{ color: C(pressHoverColor('System/Light/Blue')) }, font.Body]}>{i18n.t('Cancel')}</Text>
                </View>
              )}
            </TouchableElement>
          </View>
          <View style={{ position: 'absolute', zIndex: 1000, right: 0 }}>
            <TouchableElement
              onPress={() => {
                if (!selectedItem) {
                  return;
                }
                updateData('selectedType', selectedItem);
                setMode(MODES.CONNECTOR_DETAILS);
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15), paddingRight: scale(isTabletOrWeb ? 0 : 15) }]}>
                  <Text style={[{ color: C(pressHoverColor(selectedItem ? 'Link/Light/Primary' : 'Label/Light/Quinary')) }, font.Headline]}>
                    {i18n.t('Next')}
                  </Text>
                </View>
              )}
            </TouchableElement>
          </View>
        </View>
        <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
          <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>
            {i18n.t('Production Connector')}
          </Text>
        </View>
      </View>
      <View style={{ marginTop: scale(12), paddingHorizontal: scale(15) }}>
        {!isError && <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('SELECT THE SOFTWARE TO CONNECT')}</Text>}
      </View>
      {isError && (
        <View style={{ height: scale(126), justifyContent: 'center', alignItems: 'center' }}>
          <Text style={[font.Body, { color: C('Label/Light/Error') }]}>
            {i18n.t('Something didn’t work')}. {i18n.t('Please try again later')}.
          </Text>
        </View>
      )}
      {!isError && (
        <>
          {loaded ? (
            <View
              style={[
                { marginTop: scale(5), borderRadius: scale(SCREEN_BORDER_RADIUS), backgroundColor: 'Background/Light/Field' },
                isPhone && {
                  borderColor: C('Hairline/Light/No trans'),
                  borderBottomWidth: StyleSheet.hairlineWidth,
                  borderTopWidth: StyleSheet.hairlineWidth,
                },
              ]}
            >
              {list.map((item, index) => (
                <LineButton
                  key={item.code}
                  {...{
                    C,
                    onPress: () => setSelectedItem(item),
                    text: item.code,
                    color: 'Label/Light/Primary',
                    borderRadiusTop: isPhone ? false : index === 0,
                    borderRadiusBottom: isPhone ? false : index === list.length - 1,
                    shadow: false,
                    borderBottom: index !== list.length - 1,
                    selected: item.code === selectedItem?.code,
                  }}
                />
              ))}
            </View>
          ) : (
            <View style={{ height: scale(126), justifyContent: 'center', alignItems: 'center' }}>
              <SmallLoading />
            </View>
          )}
        </>
      )}
    </View>
  );
};

export default SelectConnectorType;
